'use client';

import {keeperFetchResponse} from '@mail-core/dashboard/keeper/binding/fetch';
import {SplashScreen} from '@shared/SplashScreen/SplashScreen';
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import {useSearchParams} from 'next/navigation';
import {useEffect, useState} from 'react';

import {OmicronContext} from '@dev-landing/components/OmicronClientContext/OmicronClientContext';

import {omicronFallback} from '@dev-landing/omicron/fetchOmicron';
import {
  OmicronConfigSchema,
  OmicronResponse,
} from '@dev-landing/omicron/omicron.entity';
import {makeResponseSafe} from '@utils/omicron';
import {applyOmicronConfigOverridesFromQuery} from '@utils/omicron/applyOmicronConfigOverridesFromQuery';
import {getDeviceInfo} from '@utils/omicron/getDeviceInfo';
import {setOidSegments} from '@utils/omicron/setOidSegments';

type Props = {
  omicronUrl?: string;
  omicronUrlConfigId?: string;
  omicronUrlEnv?: string;
  children: React.ReactNode;
};

const timeoutMillis = 5_000;

export const OmicronClientProvider = ({
  children,
  omicronUrl,
  omicronUrlConfigId,
  omicronUrlEnv,
}: Props) => {
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [omicronResponse, setOmicronResponse] = useState<OmicronResponse>();
  const {os, device} = getDeviceInfo();

  useEffect(() => {
    const configId = omicronUrlConfigId as string;
    const env = omicronUrlEnv as string;
    const params = new URLSearchParams({
      device_type: device,
      os: os,
      config_id: configId,
      app_env: env,
      service: 'vk-store-dev-landing',
    });

    const abortController = new AbortController();
    const signal = abortController.signal;

    const timeoutId = setTimeout(() => {
      abortController.abort();
      setIsLoading(false);
    }, timeoutMillis);

    keeperFetchResponse(
      fetch(`${omicronUrl}/api/v1/get?${params.toString()}`, {
        signal,
        credentials: 'include',
      }),
      'json',
    )
      .then((res: OmicronResponse) => {
        setOidSegments(res);
        return makeResponseSafe(OmicronConfigSchema, res);
      })
      .catch(() => omicronFallback)
      .then((omicronResponse) => {
        applyOmicronConfigOverridesFromQuery({
          omicronResponse,
          schema: OmicronConfigSchema,
          searchParams,
        });
        setOmicronResponse(omicronResponse);
        clearTimeout(timeoutId);
        setIsLoading(false);
      });

    return () => {
      abortController.abort();
    };
  }, [device, omicronUrl, omicronUrlConfigId, omicronUrlEnv, os]);

  return (
    <OmicronContext.Provider value={omicronResponse as OmicronResponse}>
      {isLoading && <SplashScreen />}
      {children}
    </OmicronContext.Provider>
  );
};
